<div id="auditHeader">
  <h3 class="headline-small">{{ 'audit.title' | translate }}</h3>
  <div id="auditHeaderActions">
    <div id="filterControl">
      <button mat-icon-button id="auditFilterButton" (click)="toggleFilters()"
              [attr.aria-label]="'audit.filters.open' | translate" [attr.aria-expanded]="filterOpen()">
        <mat-icon class="material-symbols-outlined">filter_list</mat-icon>
        @if (activeFilterCount() > 0) {
          <span id="activeFilterBadge">{{ activeFilterCount() }}</span>
        }
      </button>
      @if (filterOpen()) {
        <div id="auditFilterMenu">
          <span class="filterSectionLabel">{{ 'audit.filters.person' | translate }}</span>
          <button class="filterOption" (click)="selectActor(null)">
            <mat-icon class="material-symbols-outlined">{{ selectedActorId() === null ? 'check' : '' }}</mat-icon>
            <span>{{ 'audit.filters.allPeople' | translate }}</span>
          </button>
          @for (actor of actorOptions(); track actor.id) {
            <button class="filterOption" (click)="selectActor(actor.id)">
              <mat-icon class="material-symbols-outlined">{{ selectedActorId() === actor.id ? 'check' : '' }}</mat-icon>
              <span>
                {{ actor.displayName?.trim() || ('audit.actorTypes.' + actor.type | translate) }}
              </span>
            </button>
          }
          <div class="filterDivider"></div>
          <span class="filterSectionLabel">{{ 'audit.filters.period' | translate }}</span>
          @for (period of ['ALL', 'TODAY', 'LAST_7_DAYS', 'LAST_30_DAYS', 'CUSTOM']; track period) {
            <button class="filterOption" (click)="selectPeriod($any(period))">
              <mat-icon class="material-symbols-outlined">{{ selectedPeriod() === period ? 'check' : '' }}</mat-icon>
              <span>{{ 'audit.filters.periods.' + period | translate }}</span>
            </button>
          }
          @if (selectedPeriod() === 'CUSTOM') {
            <div id="customDateFields">
              <label>
                <span>{{ 'audit.filters.from' | translate }}</span>
                <input type="date" [ngModel]="customFrom()"
                       (ngModelChange)="setCustomDate('from', $event)"/>
              </label>
              <label>
                <span>{{ 'audit.filters.to' | translate }}</span>
                <input type="date" [ngModel]="customTo()"
                       (ngModelChange)="setCustomDate('to', $event)"/>
              </label>
              @if (customDatesInvalid()) {
                <span id="customDateError">{{ 'audit.filters.invalidDateRange' | translate }}</span>
              }
            </div>
          }
          <div class="filterDivider"></div>
          <button mat-button id="resetFiltersButton" (click)="resetFilters()">
            {{ 'audit.filters.reset' | translate }}
          </button>
        </div>
      }
    </div>
    <button mat-icon-button [matMenuTriggerFor]="exportMenu"
            [disabled]="!!exporting() || !exportAuditLink() || customDatesInvalid()"
            [attr.aria-label]="'audit.export.open' | translate">
      <mat-icon class="material-symbols-outlined">download</mat-icon>
    </button>
    <mat-menu #exportMenu="matMenu">
      <span class="exportMenuTitle">{{ 'audit.export.title' | translate }}</span>
      @for (format of exportFormats; track format.value) {
        <button mat-menu-item (click)="exportAudit(format.value)" [disabled]="!!exporting()">
          {{ format.label }}
        </button>
      }
    </mat-menu>
    <button mat-icon-button (click)="close()" [attr.aria-label]="'audit.close' | translate">
      <mat-icon class="material-symbols-outlined">close</mat-icon>
    </button>
  </div>
</div>

<div id="auditSummary">
  <span class="body-small">
    @if (activeFilterCount() > 0) {
      {{ 'audit.filteredCount' | translate:{filtered: page().totalElements, total: unfilteredTotal()} }}
    } @else {
      {{ 'audit.count' | translate:{count: page().totalElements} }}
    }
  </span>
  <span class="body-small">{{ 'audit.newestFirst' | translate }}</span>
</div>

@if (activeFilterCount() > 0) {
  <div id="activeFilters">
    @if (selectedActor(); as actor) {
      <button class="filterChip" (click)="selectActor(null)">
        <span>{{ actor.displayName?.trim() || ('audit.actorTypes.' + actor.type | translate) }}</span>
        <mat-icon class="material-symbols-outlined">close</mat-icon>
      </button>
    }
    @if (hasActivePeriod()) {
      <button class="filterChip" (click)="removePeriodFilter()">
        <span>
          @if (selectedPeriod() === 'CUSTOM') {
            @if (customFrom()) {
              {{ customFrom() | date:'dd.MM.yyyy' }}
            }
            @if (customFrom() && customTo()) {
              –
            }
            @if (customTo()) {
              {{ customTo() | date:'dd.MM.yyyy' }}
            }
          } @else {
            {{ 'audit.filters.periods.' + selectedPeriod() | translate }}
          }
        </span>
        <mat-icon class="material-symbols-outlined">close</mat-icon>
      </button>
    }
  </div>
}

<div id="auditSeparator"></div>

<div id="auditScroller">
  @if (loading()) {
    @for (item of [1, 2, 3]; track item) {
      <div class="auditEntry skeleton">
        <div class="actorAvatar skeletonPulse"></div>
        <div class="entryContent">
          <div class="skeletonLine skeletonPulse"></div>
          <div class="skeletonLine short skeletonPulse"></div>
        </div>
      </div>
    }
  } @else if (loadError()) {
    <div class="auditState">
      <mat-icon class="material-symbols-outlined">error_outline</mat-icon>
      <p class="body-medium">{{ 'audit.loadError.message' | translate }}</p>
      <button mat-button (click)="retryLoad()">{{ 'audit.loadError.retry' | translate }}</button>
    </div>
  } @else if (records().length === 0) {
    <div class="auditState">
      <mat-icon class="material-symbols-outlined">history</mat-icon>
      <p class="body-medium">
        {{ (activeFilterCount() > 0 ? 'audit.emptyState.filtered' : 'audit.emptyState.default') | translate }}
      </p>
    </div>
  } @else {
    @for (group of dayGroups(); track group.key) {
      <h4 class="auditDay">
        @if (group.today) {
          {{ 'audit.days.today' | translate }}
        } @else if (group.yesterday) {
          {{ 'audit.days.yesterday' | translate }}
        } @else {
          {{ group.date | date:'dd.MM.yyyy' }}
        }
      </h4>
      @for (record of group.records; track record.id) {
        <article class="auditEntry">
          <div class="actorAvatar" [class.system]="record.actor?.type === 'SYSTEM'">
            @if (record.actor?.type === 'SYSTEM') {
              <mat-icon class="material-symbols-outlined">bolt</mat-icon>
            } @else {
              {{ actorInitials(record.actor) || '?' }}
            }
          </div>
          <div class="entryContent">
            <div class="body-medium entryMessage">
              <strong>
                {{ record.actor?.displayName?.trim() || ('audit.actorTypes.' + (record.actor?.type || 'UNKNOWN') | translate) }}
              </strong>
              {{ 'audit.events.' + record.eventType | translate:eventParams(record) }}
            </div>
            <time class="body-small">{{ record.occurredAt | date:'HH:mm' }}</time>
            @if (isExpanded(record.id)) {
              <dl class="entryDetails">
                <div>
                  <dt>{{ 'audit.details.objectType' | translate }}</dt>
                  <dd>{{ 'audit.objectTypes.' + record.objectType | translate }}</dd>
                </div>
                <div>
                  <dt>{{ 'audit.details.category' | translate }}</dt>
                  <dd>{{ 'audit.categories.' + record.category | translate }}</dd>
                </div>
                @if (record.entryPoint) {
                  <div>
                    <dt>{{ 'audit.details.entryPoint' | translate }}</dt>
                    <dd>{{ record.entryPoint }}</dd>
                  </div>
                }
                @if (record.correlationId) {
                  <div>
                    <dt>{{ 'audit.details.correlationId' | translate }}</dt>
                    <dd>{{ record.correlationId }}</dd>
                  </div>
                }
                @if (record.available === false) {
                  <div>
                    <dt>{{ 'audit.details.availability' | translate }}</dt>
                    <dd>{{ 'audit.details.unavailable' | translate }}</dd>
                  </div>
                } @else if (record.available === null || record.available === undefined) {
                  <div>
                    <dt>{{ 'audit.details.availability' | translate }}</dt>
                    <dd>{{ 'audit.details.availabilityUnknown' | translate }}</dd>
                  </div>
                }
              </dl>
              @if (record.details) {
                <div class="technicalDetails">
                  <span class="label-small">{{ 'audit.details.technical' | translate }}</span>
                  <pre>{{ record.details | json }}</pre>
                </div>
              }
            }
            <button class="expandButton body-small" (click)="toggleRecord(record.id)"
                    [attr.aria-expanded]="isExpanded(record.id)">
              {{ (isExpanded(record.id) ? 'audit.showLess' : 'audit.showMore') | translate }}
            </button>
          </div>
        </article>
      }
    }
    @if (hasMore()) {
      <button mat-stroked-button id="loadMoreButton" (click)="loadMore()" [disabled]="loadingMore()">
        {{ (loadingMore() ? 'audit.loadingMore' : 'audit.loadMore') | translate }}
      </button>
    }
  }
</div>
